/**
 * Nexus live-scan overlay guard (false-positive fix, 2026-06-21).
 *
 * Why this module exists
 * ----------------------
 * During a live Dex Live Scan the app paints its OWN green status HUD/card over
 * the captured Pokémon GO screen (see `nexPip` / `nexPip3DBridge`). That card
 * shows lines like:
 *
 *     ● Scanning…
 *     6 dex entries updated
 *     Added:
 *     #077 #025 #143 #074 #892 #869
 *
 * The capture pipeline OCRs the whole frame, so when a cell number-ROI lands on
 * top of that card the OCR reads OUR OWN overlay text and parses `#077`/`#025`/…
 * as if they were Pokémon GO grid dex numbers. Those phantom numbers were then
 * applied as caught entries — the real-device false positives in the 2026-06-21
 * telemetry (gmax adds [77,25] / [143,74], perfect adds [817,818,819,821,810]…)
 * and the persisted Perfect/100% entries 810/817/818/819/821 in IMG_1223.
 *
 * This module is PURE and DOM-free. It owns two guards the OCR loop calls:
 *
 *   1. `containsNexusOverlayMarker(text)` — a HARD text guard. Any OCR text that
 *      carries a Nexus-HUD phrase ("Added:", "NEXUS DEX", "Scanning",
 *      "dex entries", "Hold steady") cannot contribute a dex number, regardless
 *      of geometry. This is the backstop: even if the card is parked somewhere
 *      the rect detector misses, its TEXT can never become a number.
 *
 *   2. `detectNexusOverlayRect(rgba)` — geometry. The Nexus card is a DARK,
 *      GREEN-TINTED rounded slab (the brand green ~ rgb(31,122,80) darkened),
 *      visually distinct from the pink/cyan/peach PoGo dex grid. We locate its
 *      bounding rect so the OCR loop can SKIP any cell ROI it materially
 *      overlaps (classificationSkipReason `nexus_overlay_overlap`) — preferring
 *      a false negative (let the user hold steady / scroll for a clean frame)
 *      over a false positive.
 *
 * The detector is deliberately specific to the GREEN Nexus card so it does NOT
 * fight the existing dark-PiP detector in dexAnchorDetector (that one masks a
 * neutral-dark native PiP window); a green-tint requirement keeps this guard
 * from masking the Pokémon GO dex pill/carousel or a neutral PiP.
 */

/** A rectangle in fractional frame coordinates (0..1), x→right, y→down. */
export interface NexusOverlayRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Phrases the Nexus live-scan HUD/card renders. Matched case-insensitively
 * against OCR text after collapsing whitespace. Kept narrow and specific to the
 * overlay so a legitimate Pokémon GO grid read is never vetoed.
 */
const NEXUS_OVERLAY_MARKERS: readonly RegExp[] = [
  /\badded\s*:/i, // "Added:" — the phantom-number row label
  /\bnexus\s*dex\b/i, // "NEXUS DEX" watermark
  /\bscanning\b/i, // "● Scanning…"
  /\bdex\s*entries\b/i, // "N dex entries updated"
  /\bhold\s*steady\b/i, // "Hold steady" guidance line
  /\bentries\s*updated\b/i, // "dex entries updated" tail
];

/**
 * True when OCR `text` contains any Nexus-overlay marker phrase. The OCR loop
 * calls this on every number crop's raw text: a match means the crop sampled our
 * own HUD, so its digits must NOT be parsed into a dex number.
 */
export function containsNexusOverlayMarker(text: string | null | undefined): boolean {
  if (!text) return false;
  const collapsed = text.replace(/\s+/g, " ");
  return NEXUS_OVERLAY_MARKERS.some((re) => re.test(collapsed));
}

/** Fraction of rect `a` that lies inside rect `b` (0..1). */
export function overlayOverlapFraction(a: NexusOverlayRect, b: NexusOverlayRect): number {
  const ix = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
  const iy = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
  const inter = ix * iy;
  const area = a.w * a.h;
  if (area <= 0) return 0;
  return inter / area;
}

/**
 * A cell ROI overlapping the Nexus overlay by more than this fraction is skipped
 * rather than classified — the digits under the card are our own HUD text, not
 * grid evidence. 0.18 mirrors the dexOcrRoi MAX_TOLERATED_OVERLAP so a ROI that
 * merely clips the card's rounded corner still reads.
 */
export const MAX_TOLERATED_OVERLAY_OVERLAP = 0.18;

/**
 * The same test for a cell's SPRITE BODY, which needs a far stricter bar.
 *
 * A number ROI can afford 18%: the digits are small and centred, and a ROI that
 * clips the card's rounded corner still reads correctly. A sprite verdict cannot,
 * because it is a STATISTIC OVER THE WHOLE REGION — chroma fraction, foreground
 * mass, body fraction. Filling even a slice of that region with our own opaque
 * HUD moves every one of those numbers at once.
 *
 * Measured on the labelled shiny pass (device trace + screenshot IMG_3795): the
 * HUD card's bottom edge overlapped the TOP grid row's sprite band by 13% of its
 * height, which after the card's horizontal extent (x from 0.409) came to 0% / 5%
 * / 13% area overlap for columns 1 / 2 / 3-4. All three sit under 0.18, so the
 * guard fired exactly ONCE in a 222-frame session while ten false writes passed
 * through it — and those landed in columns 0/1/5/4, the ordering that geometry
 * predicts. They read chroma 0.52-0.56 at ~7,700 foreground pixels against a
 * real-catch median of 0.127: not a faint sprite misjudged, our own card measured
 * as one.
 *
 * 0.02 rather than 0 leaves room for a rounded corner and edge anti-aliasing
 * while admitting no meaningful area. A cell skipped here emits NO observation,
 * so the cost is at most a deferral: the grid scrolls it out of the contaminated
 * band and a later frame classifies it cleanly.
 */
export const MAX_SPRITE_BODY_OVERLAY_OVERLAP = 0.02;

/**
 * Safety margin added to a DETECTED overlay rect before the sprite-body test,
 * as a fraction of frame height/width.
 *
 * `detectNexusOverlayRect` finds the card by its green pixels, so it measures
 * where the card is OPAQUE, not where it ends: an anti-aliased edge, a rounded
 * corner, or the card's drop shadow all fall outside the detected slab while
 * still polluting a sprite ROI. On the 07:20 trace the detector put the card's
 * top at 0.122 when the real card (screenshot IMG_3795) starts at ~0.07 — a
 * 0.05 under-measure on that edge.
 *
 * The bottom edge matched well there, but with a 0.02 area bar a few pixels of
 * error is the difference between tripping the guard and not, so the rect is
 * grown before the comparison. Detection-side geometry is left untouched: this
 * pads the COMPARISON only, and only for the sprite body — the number ROI keeps
 * using the raw rect at its own looser threshold.
 */
export const OVERLAY_RECT_SAFETY_PAD = 0.01;

/**
 * Grow a rect by `pad` on every side, clamped to the frame. Used to convert a
 * detected (opaque-pixels) overlay rect into the CONSERVATIVE rect the sprite
 * body must clear — see OVERLAY_RECT_SAFETY_PAD.
 */
export function padOverlayRect(
  rect: NexusOverlayRect,
  pad: number = OVERLAY_RECT_SAFETY_PAD,
): NexusOverlayRect {
  const x = Math.max(0, rect.x - pad);
  const y = Math.max(0, rect.y - pad);
  return {
    x,
    y,
    w: Math.min(1 - x, rect.w + pad * 2),
    h: Math.min(1 - y, rect.h + pad * 2),
  };
}

// ── HUD card geometry detection ─────────────────────────────────────────────

/** Dark-green Nexus ink: headings, the scanned chips, the footer. */
function isHudInk(r: number, g: number, b: number): boolean {
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  if (lum < 130 && g - r >= 25 && g - b >= 8) return true; // dark green
  return r >= 150 && g <= 95 && b <= 95 && r - g >= 80; // the red "not caught" chips
}
/** The card's pale mint fill (also its translucent watermark). */
function isHudFill(r: number, g: number, b: number): boolean {
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  return lum >= 208 && g - r >= 6 && g - b >= 6 && (mx - mn) / mx <= 0.26;
}
/**
 * The Nexus live-scan HUD card's rect (pale mint since 2026-08-20; the old
 * detector looked for the dark-green card and returned null on every frame for
 * a month, so every HUD-occlusion gate was dead). Found by appearance, wherever
 * the trainer dragged it: the dark-green / red ink of its heading, scanned
 * chips and footer clusters into rows no page text matches (a page label is one
 * short word); the card's pale mint fill is then followed outward from that
 * ink, a little past it on each side. Landscape on Pokédex pages, portrait on
 * Pokémon pages: neither shape is assumed. Measured within 1% of the card's
 * edges (right edge 3% short before the padding) on 26 device frames, 2-8 ms.
 */
export function detectNexusOverlayRect(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): NexusOverlayRect | null {
  if (width < 40 || height < 40) return null;
  const inkRows = new Int32Array(height);
  const minX = new Int32Array(height).fill(width), maxX = new Int32Array(height).fill(-1);
  for (let y = 0; y < height; y++) {
    let n = 0; const base = y * width;
    for (let x = 0; x < width; x++) { const i = (base + x) * 4; if (isHudInk(data[i], data[i + 1], data[i + 2])) { n++; if (x < minX[y]) minX[y] = x; if (x > maxX[y]) maxX[y] = x; } }
    inkRows[y] = n;
  }
  // Rows of the CHIP GRID / heading carry a lot of ink; a page label ("KANTO",
  // teal on the Purified page) is a single short word and never clusters.
  const rowMin = Math.max(6, Math.floor(width * 0.04));
  // largest cluster of ink rows, gap tolerant
  let bestTop = -1, bestBot = -1, bestScore = 0, curTop = -1, curScore = 0, gap = 0; const GAP = Math.floor(height * 0.03);
  for (let y = 0; y <= height; y++) {
    const on = y < height && inkRows[y] >= rowMin;
    if (on) { if (curTop < 0) { curTop = y; curScore = 0; } curScore += inkRows[y]; gap = 0; }
    else if (curTop >= 0) { gap++; if (gap > GAP || y === height) { const bot = y - gap; if (curScore > bestScore) { bestScore = curScore; bestTop = curTop; bestBot = bot; } curTop = -1; gap = 0; } }
  }
  if (bestTop < 0 || bestBot - bestTop < height * 0.03) return null;
  // Horizontal extent: 3rd / 97th percentile of ink columns over the cluster rows.
  const colHist = new Int32Array(width);
  let inkTotal = 0;
  for (let y = bestTop; y <= bestBot; y++) { if (inkRows[y] < rowMin) continue; const base = y * width; for (let x = 0; x < width; x++) { const i = (base + x) * 4; if (isHudInk(data[i], data[i + 1], data[i + 2])) { colHist[x]++; inkTotal++; } } }
  let x0 = 0, x1 = width - 1, acc = 0;
  for (let x = 0; x < width; x++) { acc += colHist[x]; if (acc >= inkTotal * 0.03) { x0 = x; break; } }
  acc = 0;
  for (let x = width - 1; x >= 0; x--) { acc += colHist[x]; if (acc >= inkTotal * 0.03) { x1 = x; break; } }
  if (x1 - x0 < width * 0.15) return null;
  const fillFrac = (y: number, xa: number, xb: number) => { let n = 0; for (let x = xa; x < xb; x++) { const i = (y * width + x) * 4; if (isHudFill(data[i], data[i + 1], data[i + 2])) n++; } return n / Math.max(1, xb - xa); };
  const colFill = (x: number, ya: number, yb: number) => { let n = 0; for (let y = ya; y < yb; y++) { const i = (y * width + x) * 4; if (isHudFill(data[i], data[i + 1], data[i + 2])) n++; } return n / Math.max(1, yb - ya); };
  // The fill spans are measured over the ink extent inset a little (the card is wider than its text).
  const ix0 = x0 + Math.floor((x1 - x0) * 0.1), ix1 = x1 - Math.floor((x1 - x0) * 0.1);
  const capX = Math.floor(width * 0.06), capY = Math.floor(height * 0.03);
  let top = bestTop, bot = bestBot; let miss = 0;
  while (top > Math.max(0, bestTop - capY) && miss < 3) { if (fillFrac(top - 1, ix0, ix1) >= 0.7 || inkRows[top - 1] >= rowMin) { top--; miss = 0; } else { miss++; top--; } }
  top += miss; miss = 0;
  while (bot < Math.min(height - 1, bestBot + capY) && miss < 3) { if (fillFrac(bot + 1, ix0, ix1) >= 0.7 || inkRows[bot + 1] >= rowMin) { bot++; miss = 0; } else { miss++; bot++; } }
  bot -= miss;
  const iy0 = top + Math.floor((bot - top) * 0.15), iy1 = bot - Math.floor((bot - top) * 0.15);
  let left = x0, right = x1; miss = 0;
  while (left > Math.max(0, x0 - capX) && miss < 3) { if (colFill(left - 1, iy0, iy1) >= 0.7) { left--; miss = 0; } else { miss++; left--; } }
  left += miss; miss = 0;
  while (right < Math.min(width - 1, x1 + capX) && miss < 3) { if (colFill(right + 1, iy0, iy1) >= 0.7) { right++; miss = 0; } else { miss++; right++; } }
  right -= miss;
  const w = right - left + 1, h = bot - top + 1;
  if (w < width * 0.25 || h < height * 0.06) return null;
  // The card's padding beyond its last ink / fill column: a little slack on every side.
  const padX = Math.floor(width * 0.02), padY = Math.floor(height * 0.008);
  const L = Math.max(0, left - padX), R = Math.min(width - 1, right + padX), T = Math.max(0, top - padY), B = Math.min(height - 1, bot + padY);
  return { x: L / width, y: T / height, w: (R - L + 1) / width, h: (B - T + 1) / height };
}
